import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { selectConfig } from '../../redux/slices/configSlice';
import { setDafaplusPreChatStep } from '../../redux/slices/pageSlice';
import { RootState } from '../../redux/store';
import type { Conversation } from '@twilio/conversations';
import useHeaderDafaplusStyles from './HeaderDafaplusStyles';
import { sessionDataHandler } from '../../sessionDataHandler';
import {
  DAFAPLUS_LANGUAGE_STORAGE_KEY,
  getDafaplusLanguageText,
} from '../PreChat/dafaplusLanguage';

const getDafaplusLanguageFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const pathParts = window.location.pathname
    .split('/')
    .filter((part) => part)
    .map((part) => part.toUpperCase());

  const pathLanguage = pathParts.find((part) => ['EN', 'IN', 'MY', 'KR', 'TH'].includes(part));

  if (pathLanguage) {
    return pathLanguage;
  }

  return window.location.search ? new URLSearchParams(window.location.search).get('lang') : null;
};

export default function HeaderDafaplus() {
  const classes = useHeaderDafaplusStyles();
  const dispatch = useDispatch();
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [openCallDialog, setOpenCallDialog] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Get Twilio conversation from Redux
  const conversation = useSelector((state: RootState) => (state as any)?.twilioChat?.conversation) as Conversation | undefined;
  const currentPage = useSelector((state: RootState) => state.page.currentPage);
  const dafaplusPreChatStep = useSelector((state: RootState) => state.page.dafaplusPreChatStep);
  const isMobileView = useMediaQuery('(max-width:425px)');

  const config = useSelector(selectConfig);
  const formData = config?.data?.length ? config.data[0].attributes : null;
  const selectedLanguage = typeof window !== 'undefined'
    ? (localStorage.getItem(DAFAPLUS_LANGUAGE_STORAGE_KEY) || getDafaplusLanguageFromUrl())
    : null;
  const languageText = getDafaplusLanguageText(selectedLanguage);

  const brand = formData?.Template?.Brand?.toLowerCase();
  const isCenteredBrand = ['zipang', 'sevenbaby', 'jamboree', 'dafaplus'].includes(brand);

  // hide logo broken image
  const handleImageLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  // Handle browser/tab close events
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (conversation) {
        console.log('🚪 Browser/tab closing - calling conversation.leave()');
        conversation.leave().then(() => {
          console.log('✅ Successfully left conversation');
        }).catch((error) => {
          console.error('❌ Error leaving conversation:', error);
        });
      }
    };

    const handleUnload = () => {
      if (conversation) {
        console.log('🚪 Page unloading - final attempt to leave conversation');
        try {
          conversation.leave();
        } catch (error) {
          console.error('❌ Error in unload leave:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [conversation]);

  // Close button handlers
  const handleCloseButtonClick = () => {
    setOpenCloseDialog(true);
  };

  const handleConfirmLeave = async () => {
    setOpenCloseDialog(false);

    if (conversation) {
      await conversation.leave();
    }

    sessionDataHandler.clear();
    window.location.reload();

  };

  const handleCancelLeave = () => {
    setOpenCloseDialog(false);
  };

  // Call button handlers
  const handleCallButtonClick = () => {
    setOpenCallDialog(true);
  };

  const handleConfirmCall = () => {
    setOpenCallDialog(false);

    const currentUrl = new URL(window.location.href);
    const pathParts = currentUrl.pathname.split('/').filter(part => part);
    const lang = pathParts[1];
    let brand = pathParts[2];

    // Construct the new URL for Dafaplus call routing
    let navigateUrl = `https://www.cs-livechat.com/ccaas/v2/call/${brand}?lang=${lang}`;
    const sParam = currentUrl.searchParams.get('s');
    const level = currentUrl.searchParams.get('level');

    if (sParam) {
      navigateUrl += `&s=${sParam}`;
    }
    if (level) {
      navigateUrl += `&level=${level}`;
    }

    window.location.href = navigateUrl;
  };

  const handleCancelCall = () => {
    setOpenCallDialog(false);
  };

  const showBackButton = currentPage === 'chat' || (currentPage === 'pre-chat' && dafaplusPreChatStep === 'category');
  const showChatTitle = currentPage === 'chat';

  if (isMobileView && currentPage === 'pre-chat') {
    return null;
  }

  const handleBackClick = () => {
    if (currentPage === 'chat') {
      handleCloseButtonClick();
      return;
    }

    dispatch(setDafaplusPreChatStep('faq'));
  };

  return (
    <Box className={classes.headerRoot}>
      <Box className={classes.leftSlot}>
        {showBackButton && (
          <IconButton
            onClick={handleBackClick}
            className={classes.navButton}
            title="Back"
          >
            <ArrowBackIosNewIcon className={classes.backIcon} />
          </IconButton>
        )}
      </Box>

      {showChatTitle ? (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            zIndex: 0,
          }}
        >
          {languageText.supportChatTitle}
        </Box>
      ) : (
        isCenteredBrand && (
          <Box className={classes.logoCentered}>
            <Box
              component="img"
              className={classes.logoImg}
              alt={formData?.Template?.BrandLogoMobile?.data?.attributes?.alternativeText || 'Logo image'}
              src={formData?.Template?.BrandLogoMobile?.data?.attributes?.url || ''}
              style={{ visibility: loaded ? 'visible' : 'hidden', opacity: loaded ? 1 : 0 }}
              onLoad={handleImageLoad}
            />
          </Box>
        )
      )}

      {/* Action Buttons */}
      {/*<Box className={classes.actionsBox}>
        <IconButton
          onClick={handleCloseButtonClick}
          className={`${classes.navButton} ${classes.closeButton}`}
          title="Close"
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </Box>*/}

      {/* Close Dialog */}
      <Dialog open={openCloseDialog} onClose={handleCancelLeave}>
        <DialogTitle className={classes.dialogTitle}>{languageText.closeChatTitle}</DialogTitle>
        <DialogContent>
          {languageText.closeChatConfirm}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave} variant="outlined">
            {languageText.closeNo}
          </Button>
          <Button onClick={handleConfirmLeave} variant="contained" color="error">
            {languageText.closeYes}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Call Dialog */}
      <Dialog open={openCallDialog} onClose={handleCancelCall}>
        <DialogTitle className={classes.dialogTitle}>Call Us</DialogTitle>
        <DialogContent>
          You will be redirected to our call center.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCall} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmCall} variant="contained" color="primary">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
